import React, { useEffect, useState } from 'react';
import './ListeBadge.css';
import {
  Autocomplete,
  Box,
  TextField,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useBackground } from '../context/BackgroundContext';
import Api from '../utils/Api';
import BadgePopup from '../composant/Dashboard/BadgePopup';
import Loading from '../composant/Loading/LoadingComponent';

/**
 * Page affichant les badges sous forme de grille visuelle.
 * Les badges obtenus sont affichés normalement, les non obtenus sont grisés.
 * Des filtres checkbox permettent d'afficher uniquement les obtenus ou non obtenus.
 * Un tooltip affiche la description au survol.
 * La date d'obtention est affichée sous le titre pour les badges obtenus.
 *
 * Page modifié par Frédérick Bethermat - 2026-03-15
 */
export default function ListeBadge() {
  const { backgroundUrl } = useBackground();
  const [obtainedBadges, setObtainedBadges] = useState([]);
  const [missingBadges, setMissingBadges] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [filterObtained, setFilterObtained] = useState(false);
  const [filterMissing, setFilterMissing] = useState(false);

  useEffect(() => {
    let id = null;
    Api.get('/auth/current_user')
      .then((response) => {
        id = response.data.id;
        Api.get('/user/' + id + '/badges')
          .then((response) => {
            setObtainedBadges(response.data.badges ?? []);
          })
          .catch((error) => console.error(error));
        Api.get('/user/' + id + '/badges-left')
          .then((response) => {
            setMissingBadges(response.data.badges ?? []);
            setLoaded(true);
          })
          .catch((error) => console.error(error));
      })
      .catch((error) => console.error(error));
  }, []);

  /**
   * Formate une date ISO en format lisible fr-CA
   * Retourne null si la date est absente
   */
  function formatDate(dateString) {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  function handleChange(event) {
    setSearch(event.target.value);
  }

  /**
   * Construit la liste de badges à afficher selon les filtres et la recherche.
   * Chaque badge porte un flag `isObtained` pour le style.
   */
  function getVisibleBadges() {
    const searchLower = search.toLowerCase();

    const filteredObtained = obtainedBadges
      .filter((b) => b.title.toLowerCase().includes(searchLower))
      .map((b) => ({ ...b, isObtained: true }));

    const filteredMissing = missingBadges
      .filter((b) => b.title.toLowerCase().includes(searchLower))
      .map((b) => ({ ...b, isObtained: false }));

    // Si aucun filtre coché, tout afficher (obtenus d'abord, non obtenus grisés)
    if (!filterObtained && !filterMissing) {
      return [...filteredObtained, ...filteredMissing];
    }

    const result = [];
    if (filterObtained) result.push(...filteredObtained);
    if (filterMissing) result.push(...filteredMissing);
    return result;
  }

  function handleClose() {
    setOpen(false);
  }

  function handleBadgeClick(badge) {
    setSelectedBadge(badge);
    setOpen(true);
  }

  /*
   * Autocomplétion trié en ordre alphabétiquepour rechercher un utilisateur dans le classement
   *
   * @author: Félix Dupras-Simard [felixdupras@gmail.com]
   * Inspiré de MUI - https://mui.com/material-ui/react-autocomplete/#combo-box - [Consulté le 9 avril 2026]
   * Inspiré de StackOverflow - https://stackoverflow.com/questions/73582640/groupby-autocomplete-sorting-of-options-in-material-ui#73582994
   */
  function AutocompletionRechercheTri() {
    const options = [...missingBadges]
      .sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1))
      .map((badge) => `${badge.title}`);
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'left',
          mb: 2,
        }}
      >
        <Autocomplete
          id="rechercheBadge"
          freeSolo
          options={options}
          sx={{ width: 300 }}
          inputValue={search}
          onInputChange={(event, newInputValue) => setSearch(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Rechercher un badge" />
          )}
        />
      </Box>
    );
  }

  if (!loaded) return <Loading />;

  const visibleBadges = getVisibleBadges();

  return (
    <div
      className="listeBadge"
      style={{
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none',
      }}
    >
      <div className="listeBadge-container">
        <div className="listeBadge-background">
          <div className="listeBadge-title">
            <h1>Liste des badges</h1>
          </div>

          <div className="listeBadge-table">
            {/* Barre de recherche */}
            <div className="listeBadge-table-header">
              {AutocompletionRechercheTri()}
            </div>

            {/* Filtres checkbox */}
            <div className="listeBadge-filters">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterObtained}
                    onChange={(e) => setFilterObtained(e.target.checked)}
                    sx={{
                      color: '#151d53',
                      '&.Mui-checked': { color: '#151d53' },
                    }}
                  />
                }
                label="Obtenue"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterMissing}
                    onChange={(e) => setFilterMissing(e.target.checked)}
                    sx={{
                      color: '#151d53',
                      '&.Mui-checked': { color: '#151d53' },
                    }}
                  />
                }
                label="Non obtenue"
              />
            </div>

            {/* Grille de badges */}
            <div className="listeBadge-grid-wrapper">
              {visibleBadges.length === 0 ? (
                <p className="listeBadge-empty">Aucun badge trouvé.</p>
              ) : (
                <div className="listeBadge-grid">
                  {visibleBadges.map((badge) => (
                    <Tooltip
                      key={badge.id}
                      title={
                        <div className="listeBadge-tooltip-content">
                          <strong>{badge.title}</strong>
                          <p>{badge.description}</p>
                          {badge.isObtained && badge.obtained_at && (
                            <p>Obtenu le : {formatDate(badge.obtained_at)}</p>
                          )}
                        </div>
                      }
                      arrow
                      placement="top"
                    >
                      <div
                        className={`badge-item ${badge.isObtained ? 'badge-obtained' : 'badge-missing'}`}
                        onClick={() => handleBadgeClick(badge)}
                      >
                        <div
                          className="badge-circle"
                          style={{
                            boxShadow: badge.isObtained
                              ? `0 0 0 3px ${badge.category_color || '#151d53'}`
                              : 'none',
                          }}
                        >
                          <img
                            src={badge.imagePath}
                            alt={badge.title}
                            className="badge-img"
                          />
                        </div>
                        <div className="badge-label">
                          <span className="badge-title">{badge.title}</span>
                          {badge.isObtained && badge.obtained_at && (
                            <span className="badge-date">
                              {formatDate(badge.obtained_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          </div>

          <BadgePopup
            isOpen={open}
            selectedBadge={selectedBadge}
            handleClose={handleClose}
          />
        </div>
      </div>
    </div>
  );
}
